import { FC } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import clsx from "clsx";
import {
  toAbsoluteUrl,
  getIconChannelUrl,
} from "src/resources/helpers";
import { Collaboration as HandledMessageListItem } from "src/app/models/Chat";
import ChatTime, { ChatListTime } from "src/app/layout/chat-handle/components/chat-message/ChatTime";
import { ChatLastMessage } from "src/app/layout/chat-handle/components/chat-message/ChatLastMessage";
import { avatarUrl } from "src/app/modules/chat/utils";
import "src/styles/css/margin.scss";
import { RootState } from "src/setup/redux/store";

interface ChatItemProps {
  // item: HandledMessageListItem;
  item: any;
  selected: boolean;
}

const ChatItem: FC<ChatItemProps> = ({ item, selected }) => {
  const profileName = getProfileName(item);
  const totalNotif = Math.max(item?.unreadCount ?? 0, 0);
  return (
    <div
      className={clsx(
        "d-flex py-1 px-2 mx-2 rounded bg-active-secondary bg-hover-secondary cl-graywhite",
        selected === true ? "cl-7days" : ""
      )}
      style={{
        paddingLeft: "10px",
        paddingRight: "10px",
      }}
      id={item.id}
      key={item.id}
      data-testid="chat-item"
    >
      <div className="d-flex align-items-center col-11">
        {/* avatar + channel badge */}
        <div className="symbol symbol-40px symbol-circle p-0 m-0">
          <img
            src={item.profilePicture ?? avatarUrl(item.customerModel)}
            alt={item.profileName}
            width={"40px"}
            height={"40px"}
            className="border border-secondary rounded-circle"
          />
          {/* Channel icon badge(s) */}
          {item.roomsModel && item.roomsModel.length > 0 ? (
            <div
              style={{
                position: "absolute",
                bottom: -2,
                right: -2,
                display: "flex",
                flexDirection: "row",
                gap: "1px",
              }}
            >
              {getUniqueChannels(item.roomsModel).map((ch: string) => (
                <img
                  key={ch}
                  src={toAbsoluteUrl(getIconChannelUrl(ch))}
                  alt={ch}
                  style={{
                    width: "14px",
                    height: "14px",
                    objectFit: "cover",
                    borderRadius: "50%",
                    border: "1.5px solid #fff",
                    backgroundColor: "#fff",
                  }}
                />
              ))}
            </div>
          ) : item.lastInteractionChannel ? (
            <img
              src={toAbsoluteUrl(getIconChannelUrl(item.lastInteractionChannel))}
              alt={item.lastInteractionChannel}
              style={{
                position: "absolute",
                bottom: -2,
                right: -2,
                width: "14px",
                height: "14px",
                objectFit: "cover",
                borderRadius: "50%",
                border: "1.5px solid #fff",
                backgroundColor: "#fff",
              }}
            />
          ) : null}
        </div>

        {/* name and last message  */}
        <div
          className="ms-3 w-100"
          style={{ maxWidth: "100%", overflow: "hidden" }}
        >
          <Link
            to="#"
            className="fs-7 text-gray-700 mb-2 text-truncate"
            style={{
              fontWeight: "600",
              display: "block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {profileName}
          </Link>
          <div className="fw-bold text-gray-400">
            <ChatLastMessage
              lastmessage={item.lastInteractionMessage ?? ""}
              lastmessagetype={item.lastInteractionType ?? ""}
              id={item.id}
            />
          </div>
        </div>
      </div>

      <div className="d-flex flex-column align-items-end col-1 p-0 pe-1">
        {item.lastInteractionAt && (
          <div
            className={
              totalNotif > 0
                ? "text-success fs-7 mb-1 fw-bolder"
                : "text-muted fs-7 mb-1"
            }
          >
            {ChatListTime(item.lastInteractionAt.seconds)}
          </div>
        )}
        {totalNotif > 0 ? (
          <span className="badge symbol-badge badge-sm badge-circle bg-success start-75 m-0">
            {totalNotif}
          </span>
        ) : (
          <>
            {item?.lastInteractionStatus === "submitted" &&
              item.lastInteractionDestination === "outbound" && (
                <div>
                  <i className={clsx("bi bi-clock fs-3")}></i>
                </div>
              )}
            {item?.lastInteractionStatus === "sent" &&
              item.lastInteractionDestination === "outbound" && (
                <div>
                  <i className={clsx("bi bi-check2 fs-3")}></i>
                </div>
              )}
            {item?.lastInteractionStatus === "delivered" &&
              item.lastInteractionDestination === "outbound" && (
                <div>
                  <i className={clsx("bi bi-check2-all fs-3")}></i>
                </div>
              )}
            {item?.lastInteractionStatus === "read" &&
              item.lastInteractionDestination === "outbound" && (
                <div>
                  <i className={clsx("bi bi-check2-all text-primary fs-3")}></i>
                </div>
              )}
          </>
        )}
      </div>
    </div>
  );
};

function getUniqueChannels(rooms: any[]): string[] {
  const channels = new Set<string>();
  rooms.forEach((room) => {
    const type = room?.accountData?.type || room?.channel;
    if (type) channels.add(type);
  });
  return Array.from(channels);
}

function getProfileName(item: HandledMessageListItem) {
  let profileName = item.profileName;

  //if customer model empty, show profileName or customerName
  if (!item.customerModel) {
    profileName = item.profileName ?? item.phoneNumber ?? "";
  } else if (!item.customerModel?.firstName) {
    //first name not exists
    profileName = `+${item.customerModel.phoneNumber}`;
  } else if (item.customerModel?.lastName) {
    //first name exists & last name exists
    profileName = `${item.customerModel?.firstName} ${item.customerModel?.lastName}`;
  } else {
    //last name not exists
    profileName = `${item.customerModel?.firstName}`;
  }

  if (profileName.length > 35) {
    profileName = profileName.substring(0, 31) + "...";
  }

  return profileName;
}

export default ChatItem;
